import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Target } from "lucide-react";

export default function SetGoalDialog({ open, onOpenChange, currentGoalWeight, useLbs, onSave }) {
  const [weight, setWeight] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setWeight(currentGoalWeight != null && currentGoalWeight > 0 ? String(currentGoalWeight) : "");
    }
  }, [open, currentGoalWeight]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const val = parseFloat(weight);
    if (!val || val <= 0) return;
    setSaving(true);
    try {
      await onSave(val);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#131F31] border border-[#233148] text-white rounded-2xl">
        <DialogHeader>
          <div className="w-10 h-10 rounded-xl bg-[#36D0FF]/10 text-[#36D0FF] flex items-center justify-center mb-2">
            <Target className="w-5 h-5" />
          </div>
          <DialogTitle className="text-xl font-bold text-white font-display">
            {currentGoalWeight ? "Update target weight" : "Set target weight"}
          </DialogTitle>
          <DialogDescription className="text-white/60 text-sm">
            Set your target weight to track your goal completion percentage and stay motivated.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="target-weight-input" className="text-xs uppercase tracking-wider text-white/60">
              Target weight ({useLbs ? "lbs" : "kg"})
            </Label>
            <Input
              id="target-weight-input"
              type="number"
              step="0.1"
              min="20"
              max="500"
              placeholder={useLbs ? "e.g. 170" : "e.g. 77"}
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              className="bg-[#0C1424] border-[#233148] text-white text-lg h-12 focus:border-[#36D0FF]"
              required
              autoFocus
            />
          </div>

          <DialogFooter className="pt-2 flex gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-white/70 hover:text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || !weight || parseFloat(weight) <= 0}
              className="bg-[#36D0FF] hover:bg-[#36D0FF]/90 text-[#0C1424] font-semibold"
            >
              {saving ? "Saving…" : "Save goal"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
